import { Fragment, type ReactNode, useState } from "react";
import type { Task } from "@gw/shared-types";
import { fmtETDate } from "@/lib/format";
import { InfoTooltip } from "@/components/InfoTooltip";
import { ActionHistoryTable } from "./ActionHistoryTable";
import { groupTasks, queuedSummary, type TaskGroups } from "../taskGroups";
import {
  deriveTaskStatus,
  type DerivedTaskStatus,
  findDiscardAction,
  IDLE_TIP,
  isCountedTask,
  taskTotals,
} from "../taskTotals";

/** The Operations verdict on the request's open tasks, so the table agrees with the attention banner. */
export interface TaskAttentionMarks {
  /** The deciding task (first open one in FBO order). */
  sittingTaskId: string;
  /** Whole business days it has sat. */
  sittingBd: number;
  state: "stuck" | "blocked" | "stuckAndBlocked";
  /** Open tasks behind the deciding one, in FBO order. */
  queuedTaskIds: string[];
}

interface Props {
  tasks: Task[];
  /** Duration unit from the screen toggle; forwarded to the action-history Wait/Active columns. */
  unit: "bd" | "cd";
  attention?: TaskAttentionMarks;
}

/** DOM id of a task's row, so the banner's "Show task" can scroll to it. */
export function taskRowId(taskId: string): string {
  return `task-${taskId}`;
}

/**
 * Renders a bd/cd duration stack for a task metric (Wait / Execution / Duration). Shows a value
 * exactly when the task is "counted" (see isCountedTask); otherwise an em-dash — a pulsing one for
 * an in-progress (not-yet-completed) task, a plain one for discarded/not-started.
 */
function durCell(t: Task, bd: number, cd: number) {
  if (isCountedTask(t)) return bdCdStack(bd, cd);
  const status = deriveTaskStatus(t);
  return status === "Discarded" || status === "NotStarted" ? (
    <span style={{ color: "var(--fg-3)" }}>—</span>
  ) : (
    <span className="dur-running">—</span>
  );
}

/** Plain bd/cd two-line stack (no per-task guards) — used for the footer totals. */
function bdCdStack(bd: number, cd: number) {
  return (
    <div className="dur-stack">
      <span className="dur-bd">{bd.toFixed(2)} bd</span>
      <span className="dur-cd">{cd.toFixed(2)} cd</span>
    </div>
  );
}

interface GroupRowProps {
  label: string;
  tone: "done" | "now" | "queued";
  count: number;
  note?: string;
  children?: ReactNode;
}

/** The heading row of a Done / Now / Queued group; spans the whole table. */
function GroupRow({ label, tone, count, note, children }: GroupRowProps) {
  return (
    <tr className={`task-group ${tone}`}>
      <td colSpan={13}>
        <span className="task-group-label">
          {label} · <span className="count">{count}</span>
        </span>
        {note && <span className="task-group-note"> · {note}</span>}
        {children}
      </td>
    </tr>
  );
}

interface GroupToggleProps {
  open: boolean;
  count: number;
  controls: string;
  onToggle: () => void;
}

/** Show N / Hide for a group's rows. */
function GroupToggle({ open, count, controls, onToggle }: GroupToggleProps) {
  return (
    <button type="button" className="task-group-toggle" aria-expanded={open} aria-controls={controls} onClick={onToggle}>
      {open ? "Hide ▴" : `Show ${count} ▾`}
    </button>
  );
}

const STATUS_LABEL: Record<DerivedTaskStatus, string> = {
  Completed: "Completed",
  InProgress: "In progress",
  NotStarted: "Not started",
  Discarded: "Discarded",
};

export function TasksTable({ tasks, unit, attention }: Props) {
  const queued = new Set(attention?.queuedTaskIds ?? []);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Done / Now / Queued, the way the fold reads; the queue is folded until asked for.
  const groups: TaskGroups = groupTasks(tasks, attention?.sittingTaskId);
  const [queuedOpen, setQueuedOpen] = useState(false);
  const [doneOpen, setDoneOpen] = useState(true);
  // Tasks rows toggle expansion rather than navigate; activate keyboard nav anyway so keyboard
  // users can reach + expand any row.
  const toggleExpanded = (taskId: string) => {
    setExpanded((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  if (tasks.length === 0) {
    return (
      <div className="empty">
        <h4>No tasks</h4>
        <p>This request has no recorded tasks in the analytics window.</p>
      </div>
    );
  }

  // Footer totals over the counted tasks only (excludes discarded/not-started/not-completed so the
  // totals reconcile with the visible rows). Wait + Execution = Duration. Same helper feeds the
  // header's Total Task Wait / Total Task Execution.
  const totals = taskTotals(tasks);

  const renderTask = (t: Task) => {
        const exp = !!expanded[t.taskId];
        return (
          <Fragment key={t.taskId}>
            <tr
              id={taskRowId(t.taskId)}
              className={"task-row " + (exp ? "expanded " : "") + (attention?.sittingTaskId === t.taskId ? `sitting ${attention.state}` : "")}
              onClick={() => toggleExpanded(t.taskId)}
              tabIndex={0}
              aria-expanded={exp}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleExpanded(t.taskId);
                }
              }}
            >
              <td style={{ width: 28 }}>
                <span
                  className="material-symbols-rounded"
                  style={{
                    fontSize: 18,
                    color: "var(--fg-2)",
                    transform: exp ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform 150ms ease",
                  }}
                >
                  chevron_right
                </span>
              </td>
              <td>
                {(() => {
                  const derived = deriveTaskStatus(t);
                  const discardReason =
                    derived === "Discarded"
                      ? findDiscardAction(t.actions)?.reasonMessage
                      : undefined;
                  return (
                    <span
                      className={"status-pill " + derived.toLowerCase()}
                      title={discardReason || undefined}
                    >
                      <span className="dot" />
                      {STATUS_LABEL[derived]}
                    </span>
                  );
                })()}
              </td>
              <td>
                <div className="cell-primary">
                  {t.taskName}
                  {attention?.sittingTaskId === t.taskId && (
                    <span className={`sit-tag ${attention.state}`} title={`The first open task in FBO order; it decides the request's attention state.`}>
                      <span className="material-symbols-rounded" style={{ fontSize: 13 }}>report</span>
                      sitting · {attention.sittingBd} bd
                    </span>
                  )}
                  {queued.has(t.taskId) && (
                    <span className="queued-tag" title="Open, but behind the sitting task; tasks run in sequence, so it is not waiting yet.">queued</span>
                  )}
                </div>
                {t.wasReopened && (
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--gw-error-600)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span className="material-symbols-rounded" style={{ fontSize: 12 }}>
                      refresh
                    </span>
                    Reopened
                  </span>
                )}
              </td>
              <td>
                <span className="cell-secondary">{t.taskCategory}</span>
              </td>
              <td>
                <span className="cell-secondary">{t.taskType}</span>
              </td>
              <td className="numeric">{t.actorCount}</td>
              <td>
                {deriveTaskStatus(t) === "NotStarted" ? (
                  <span style={{ color: "var(--fg-3)" }}>—</span>
                ) : (
                  <span className="cell-secondary">{fmtETDate(t.startDatetime)}</span>
                )}
              </td>
              <td>
                {(() => {
                  const derived = deriveTaskStatus(t);
                  if (derived === "Discarded" || derived === "NotStarted") {
                    return <span style={{ color: "var(--fg-3)" }}>—</span>;
                  }
                  return t.completedDatetime ? (
                    <span className="cell-secondary">{fmtETDate(t.completedDatetime)}</span>
                  ) : (
                    <span className="dur-running">running</span>
                  );
                })()}
              </td>
              <td className="numeric">{durCell(t, t.idleBd, t.idleCd)}</td>
              <td className="numeric">{durCell(t, t.waitBd, t.waitCd)}</td>
              <td className="numeric">{durCell(t, t.durationBd, t.durationCd)}</td>
              <td className="numeric">
                {durCell(
                  t,
                  t.idleBd + t.waitBd + t.durationBd,
                  t.idleCd + t.waitCd + t.durationCd,
                )}
              </td>
              <td className="numeric">{t.actionCount}</td>
            </tr>
            {exp && (
              <tr>
                <td colSpan={13} style={{ padding: 0, background: "var(--gw-neutral-50)" }}>
                  <ActionHistoryTable actions={t.actions} unit={unit} />
                </td>
              </tr>
            )}
          </Fragment>
        );
  };

  return (
    <div className="tbl-wrap">
      <table className="tbl tasks-tbl">
        {/* Fixed widths on every column except the three text columns (Task/Category/Type), which
            are left auto so they absorb the remaining width. The trailing block — Wait 96 +
            Execution 96 + Duration 96 + Actions 120 = 408px — matches the action-history table's
            trailing block, so the Wait/Execution/Duration trio lines up across both. Idle (96) sits
            left of the trio, so the trio stays contiguous + rightmost and the alignment holds. */}
        <colgroup>
          <col style={{ width: 36 }} />
          <col style={{ width: 130 }} />
          <col />
          <col />
          <col />
          <col style={{ width: 70 }} />
          <col style={{ width: 120 }} />
          <col style={{ width: 120 }} />
          <col style={{ width: 96 }} />
          <col style={{ width: 96 }} />
          <col style={{ width: 96 }} />
          <col style={{ width: 96 }} />
          <col style={{ width: 120 }} />
        </colgroup>
        <thead>
          <tr>
            <th />
            <th>Task status</th>
            <th>Task</th>
            <th>Category</th>
            <th>Type</th>
            <th className="numeric">Actors</th>
            <th>Started</th>
            <th>Completed</th>
            <th className="numeric">
              Idle <InfoTooltip inline text={IDLE_TIP} />
            </th>
            <th className="numeric">Wait</th>
            <th className="numeric">Execution</th>
            <th className="numeric">Duration</th>
            <th className="numeric">Actions</th>
          </tr>
        </thead>
        {groups.done.length > 0 && (
          <tbody id="tasks-done">
            <GroupRow label="Done" tone="done" count={groups.done.length}>
              <GroupToggle open={doneOpen} count={groups.done.length} controls="tasks-done" onToggle={() => setDoneOpen((v) => !v)} />
            </GroupRow>
            {doneOpen && groups.done.map(renderTask)}
          </tbody>
        )}
        {groups.now && (
          <tbody>
            <GroupRow label="Now" tone="now" count={1} />
            {renderTask(groups.now)}
          </tbody>
        )}
        {groups.queued.length > 0 && (
          <tbody id="tasks-queued">
            <GroupRow label="Queued" tone="queued" count={groups.queued.length} note={queuedSummary(groups.queued, groups.now)}>
              <GroupToggle open={queuedOpen} count={groups.queued.length} controls="tasks-queued" onToggle={() => setQueuedOpen((v) => !v)} />
            </GroupRow>
            {queuedOpen && groups.queued.map(renderTask)}
          </tbody>
        )}
        <tfoot>
          <tr>
            <td colSpan={8} className="total-label">
              Task time <InfoTooltip inline placement="top" text={`Sum over the tasks, not the stage clock above: tasks overlap and run in parallel, so this can differ from the journey's business days. ${IDLE_TIP}`} />
            </td>
            <td className="numeric">{bdCdStack(totals.idleBd, totals.idleCd)}</td>
            <td className="numeric">{bdCdStack(totals.waitBd, totals.waitCd)}</td>
            <td className="numeric">{bdCdStack(totals.execBd, totals.execCd)}</td>
            <td className="numeric">
              {bdCdStack(
                totals.idleBd + totals.waitBd + totals.execBd,
                totals.idleCd + totals.waitCd + totals.execCd,
              )}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
